import {
    formatIncomeSplitPercentagesNew,
    type IncomeSplitConfig,
    PaycheckRoutingStatus,
} from '@fruitful/income-split';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import {
    type MutableRefObject,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    DownloadIcon,
    Icon,
    Spinner,
    SquarePenIcon,
    Switch,
    View,
    XStack,
    YStack,
} from 'smoothie';

import IncomeSplitToggleSheet from '../../../banking/components/income-split/IncomeSplitToggleSheetNew';
import useGetIncomeSplitFromGlobalState from '../../../banking/hooks/income-split/useGetIncomeSplitFromGlobalStateNew';
import useUpdateIncomeSplitStatus from '../../../banking/hooks/income-split/useUpdateIncomeSplitStatusNew';
import { incomeSplitAtom } from '../../../banking/state/PaycheckRoutingAtoms';
import { bankAccountDisplayDetails } from '../../../banking/state/SpacesAtoms';
import { isAccountValidAsIncomeSplitSource } from '../../../banking/utils/IncomeSplitUtils';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { captureWarning } from '../../../logger/Logger';
import { IncomeSplitSettingsSheet } from './IncomeSplitSettingsSheet';

interface IncomeSplitWidgetProps {
    // Set to true by the containing Money Map edit session once its overall
    // Save has succeeded, so this widget's unmount cleanup (below) knows the
    // pending income type/accounts change was actually persisted and should
    // be left alone rather than reverted.
    hasMoneyMapSavedRef?: MutableRefObject<boolean>;
}

const IncomeSplitWidget = ({ hasMoneyMapSavedRef }: IncomeSplitWidgetProps) => {
    const user = useAuthedUser();
    const { isLoading } = useGetIncomeSplitFromGlobalState();
    const [incomeSplit, setIncomeSplit] = useAtom(incomeSplitAtom);
    const bankAccounts = useAtomValue(bankAccountDisplayDetails);
    const [isToggleSheetOpen, setIsToggleSheetOpen] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const queryClient = useQueryClient();

    const { mutateAsync: updateStatus } = useUpdateIncomeSplitStatus();

    // This widget only mounts while a Money Map edit session is active, so
    // its mount/unmount lifecycle matches the edit session's. Snapshot the
    // fields the Settings sheet can change (IncomeSplitSettingsSheet.tsx
    // writes them straight into the shared incomeSplitAtom as a "pending"
    // edit meant to be bundled into the overall Money Map save) and, if the
    // session ends without that save completing, revert just those fields on
    // unmount. Toggle/status changes are excluded — they persist immediately
    // via their own mutation below and already revert themselves on cancel
    // (see IncomeSplitToggleSheetNew's hasSavedRef).
    //
    // The snapshot can't be taken via useRef's initializer: on this widget's
    // first render incomeSplit is often still null (useGetIncomeSplitFromGlobalState
    // populates the atom in its own effect, which hasn't run yet), so that
    // would permanently capture undefined and turn every revert into a no-op.
    // Instead, mirror IncomeSplitNew.tsx's originalIncomeSplit ref: capture
    // the first non-null value this effect ever sees.
    //
    // While loading, useGetIncomeSplitFromGlobalState can transiently seed
    // the atom with a placeholder ({ onlyExecuteOnPaychecks: false, accounts:
    // <all eligible accounts>, isNew: true, id: undefined }) for one render
    // before it resolves to the real persisted config — the same placeholder
    // the isNew/id check below already treats as "not a real config to edit".
    // Skip it here too, or an untouched discard would revert to it instead
    // of the true saved settings.
    const originalSettingsRef = useRef<{
        onlyExecuteOnPaychecks: boolean;
        accounts: IncomeSplitConfig['accounts'];
    } | null>(null);

    useEffect(() => {
        if (
            incomeSplit &&
            incomeSplit.isNew !== true &&
            incomeSplit.id != null &&
            !originalSettingsRef.current
        ) {
            originalSettingsRef.current = {
                onlyExecuteOnPaychecks: incomeSplit.onlyExecuteOnPaychecks,
                accounts: incomeSplit.accounts,
            };
        }
    }, [incomeSplit]);

    useEffect(() => {
        return () => {
            if (hasMoneyMapSavedRef?.current) {
                return;
            }
            const original = originalSettingsRef.current;
            if (!original) {
                return;
            }
            setIncomeSplit((current) =>
                current
                    ? {
                          ...current,
                          onlyExecuteOnPaychecks:
                              original.onlyExecuteOnPaychecks,
                          accounts: original.accounts,
                      }
                    : current,
            );
        };
        // Only run on unmount — reading the latest values via the setIncomeSplit
        // updater above, not this closure, is what keeps this correct.
    }, []);

    const eligibleAccountCount = useMemo(
        () =>
            bankAccounts.filter((account) =>
                isAccountValidAsIncomeSplitSource(user, account),
            ).length,
        [bankAccounts, user],
    );

    if (isLoading) {
        return <Spinner />;
    }

    if (incomeSplit == null) {
        return null;
    }

    // edit-mode-active is only reachable after Go workflow completion, so
    // incomeSplit should always have a persisted backend config with an id.
    if (incomeSplit.isNew === true || incomeSplit.id == null) {
        captureWarning(
            'IncomeSplitWidget rendered without a persisted config — toggle/settings disabled',
        );
        return null;
    }

    const isActive = incomeSplit.status === PaycheckRoutingStatus.ACTIVE;

    const incomeTypeLabel = incomeSplit.onlyExecuteOnPaychecks
        ? 'paychecks only'
        : 'all income';

    const accountCount = incomeSplit.accounts?.length ?? 0;
    const accountsLabel =
        accountCount === 0
            ? 'no accounts'
            : accountCount === eligibleAccountCount && eligibleAccountCount > 0
              ? 'all accounts'
              : `${accountCount} account${accountCount === 1 ? '' : 's'}`;

    const handleToggle = (checked: boolean) => {
        setIncomeSplit({
            ...incomeSplit,
            status: checked
                ? PaycheckRoutingStatus.ACTIVE
                : PaycheckRoutingStatus.INACTIVE,
        });
        setIsToggleSheetOpen(true);
    };

    const handleToggleSave = async (status: PaycheckRoutingStatus) => {
        await updateStatus(
            {
                body: { status },
                urlParams: { configId: incomeSplit.id as string },
            },
            {
                onSuccess: (result) => {
                    const formatted = formatIncomeSplitPercentagesNew(result);
                    setIncomeSplit(formatted);
                    setIsToggleSheetOpen(false);
                },
                onSettled: async () => {
                    await queryClient.invalidateQueries({
                        queryKey: ['getIncomeConfigs'],
                        exact: true,
                    });
                },
            },
        );
    };

    return (
        <>
            <YStack
                backgroundColor="$gray200"
                borderRadius={24}
                padding={16}
                gap={13}
            >
                <XStack
                    alignItems="center"
                    justifyContent="space-between"
                    gap={12}
                >
                    <XStack gap={8} alignItems="center" flex={1}>
                        <View
                            width={32}
                            height={32}
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Icon
                                icon={DownloadIcon}
                                size={24}
                                color="$gray1200"
                            />
                        </View>
                        <BodyMediumEmphasized>
                            Income Split
                        </BodyMediumEmphasized>
                    </XStack>
                    <Switch
                        isChecked={isActive}
                        onCheckedChange={handleToggle}
                        style={{ transformOrigin: 'right' }}
                    />
                </XStack>

                {isActive && (
                    <YStack
                        backgroundColor="$white"
                        borderRadius={16}
                        padding={16}
                        gap={12}
                    >
                        <XStack
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <XStack gap={8} alignItems="center">
                                <BodyMediumRegular>
                                    Apply rules to
                                </BodyMediumRegular>
                                <BodyMediumRegular color="$green1000">
                                    {incomeTypeLabel}
                                </BodyMediumRegular>
                            </XStack>
                            <View
                                minWidth={32}
                                minHeight={32}
                                alignItems="center"
                                justifyContent="center"
                                cursor="pointer"
                                onPress={() => {
                                    setIsSettingsOpen(true);
                                }}
                            >
                                <Icon
                                    icon={SquarePenIcon}
                                    size={16}
                                    color="$gray1200"
                                />
                            </View>
                        </XStack>
                        <XStack gap={8} alignItems="center">
                            <BodyMediumRegular>
                                Detect income in
                            </BodyMediumRegular>
                            <BodyMediumRegular color="$green1000">
                                {accountsLabel}
                            </BodyMediumRegular>
                        </XStack>
                    </YStack>
                )}
            </YStack>

            <IncomeSplitToggleSheet
                isOpen={isToggleSheetOpen}
                onSave={handleToggleSave}
                onClose={() => {
                    setIsToggleSheetOpen(false);
                }}
                onCancel={() => {
                    setIsToggleSheetOpen(false);
                }}
            />

            <IncomeSplitSettingsSheet
                isOpen={isSettingsOpen}
                onClose={() => {
                    setIsSettingsOpen(false);
                }}
                incomeSplit={incomeSplit}
            />
        </>
    );
};

export { IncomeSplitWidget };
